/**
 * Copy text to the system clipboard.
 * Uses the modern Clipboard API with fallback to the legacy execCommand method.
 *
 * @param {string} text - The text to copy to the clipboard
 * @returns {Promise<void>} Promise that resolves when text is copied, or rejects on error
 */
export function copyToClipboard(text) {
    // Validate input
    if (typeof text !== 'string') {
        return Promise.reject(new Error('Text must be a string'));
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).catch((error) => {
            // Fallback to legacy method if Clipboard API fails
            return fallbackCopyToClipboard(text);
        });
    }

    // Fallback to legacy method for older browsers
    return fallbackCopyToClipboard(text);
}

/**
 * Fallback method using execCommand for older browsers.
 * Creates a temporary textarea element, selects it, and copies the text.
 *
 * @param {string} text - The text to copy to the clipboard
 * @returns {Promise<void>} Promise that resolves when text is copied
 */
function fallbackCopyToClipboard(text) {
    return new Promise((resolve, reject) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);

        // Select and copy the text
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                resolve();
            } else {
                reject(new Error('Failed to copy text using execCommand'));
            }
        } catch (error) {
            document.body.removeChild(textArea);
            reject(error);
        }
    });
}
